import {
  IGroupNode,
  makeGroupPath,
} from '~/client/src/shared/resources/grouping/groupPath'
import { compareGroupsByLabel } from '~/client/src/shared/utils/compareGroupsByLabel'

export type { IGroupNode }

export interface IGroupRowInfo {
  value: string
  label: string
  path: string
  depth: number
  recordIds: string[]
  isCollapsed: boolean
}

export interface IBuildGroupedRowsParams<TRecord, TRow> {
  records: TRecord[]
  fieldKey: string
  getRecordGroupPaths: (record: TRecord) => IGroupNode[][]
  getRecordId: (record: TRecord) => string
  collapsed: Map<string, boolean>
  buildGroupRow: (group: IGroupRowInfo, rowId: string) => TRow
  buildLeafRow: (record: TRecord, rowId: string) => TRow
  /** Sibling group order by value; defaults to A-Z by label. */
  compareGroups?: (a: string, b: string) => number
}

interface MutableNode<TRecord> {
  value: string
  label: string
  path: string
  depth: number
  children: Map<string, MutableNode<TRecord>>
  directRecords: TRecord[]
  recordIds: Set<string>
}

export const buildGroupedRows = <TRecord, TRow>({
  records,
  fieldKey,
  getRecordGroupPaths,
  getRecordId,
  collapsed,
  buildGroupRow,
  buildLeafRow,
  compareGroups,
}: IBuildGroupedRowsParams<TRecord, TRow>): TRow[] => {
  const roots = new Map<string, MutableNode<TRecord>>()

  records.forEach(record => {
    const recordId = getRecordId(record)

    getRecordGroupPaths(record).forEach(path => {
      let level = roots
      let parentPath = ''
      let node: MutableNode<TRecord> | undefined

      path.forEach((groupNode, depth) => {
        const nodePath = makeGroupPath(parentPath, fieldKey, groupNode.value)
        node = level.get(groupNode.value)
        if (!node) {
          node = {
            value: groupNode.value,
            label: groupNode.label,
            path: nodePath,
            depth,
            children: new Map(),
            directRecords: [],
            recordIds: new Set(),
          }
          level.set(groupNode.value, node)
        }
        node.recordIds.add(recordId)
        parentPath = nodePath
        level = node.children
      })

      node?.directRecords.push(record)
    })
  })

  const emit = (nodes: Map<string, MutableNode<TRecord>>): TRow[] => {
    const sorted = Array.from(nodes.values()).sort((a, b) =>
      compareGroups
        ? compareGroups(a.value, b.value)
        : compareGroupsByLabel(
            a.value,
            b.value,
            value => nodes.get(value)?.label ?? '',
          ),
    )

    return sorted.flatMap(node => {
      const isCollapsed = collapsed.get(node.path) === true

      const groupRow = buildGroupRow(
        {
          value: node.value,
          label: node.label,
          path: node.path,
          depth: node.depth,
          recordIds: Array.from(node.recordIds),
          isCollapsed,
        },
        `group::${node.path}`,
      )

      if (isCollapsed) {
        return [groupRow]
      }

      const childRows = emit(node.children)
      const recordRows = node.directRecords.map(record =>
        buildLeafRow(record, `${getRecordId(record)}::${node.path}`),
      )

      // Records assigned directly to this node come before its child groups, so
      // the desktop table matches the mobile card list (and LBS) ordering.
      return [groupRow, ...recordRows, ...childRows]
    })
  }

  return emit(roots)
}
